import { useCallback, useEffect, useRef, useState } from "react";
import {
  LOCAL_STORAGE_KEYS,
  SUPRIMENTOS_KANBAN_AUTO_REFRESH_MS,
} from "@/config/constants";

function readStoredMs(key: string): number | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function writeStoredMs(key: string, value: number): void {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    /* quota / modo privado */
  }
}

export function formatKanbanCountdown(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

interface UseKanbanAutoRefreshOptions {
  apply: () => void;
  loading: boolean;
  hasData: boolean;
}

/**
 * Atualização automática do Kanban com ciclo de 15 min persistido em localStorage,
 * para que recarregar a página não reinicie o contador.
 */
export function useKanbanAutoRefresh({
  apply,
  loading,
  hasData,
}: UseKanbanAutoRefreshOptions) {
  const [secondsUntilRefresh, setSecondsUntilRefresh] = useState<number | null>(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<number | null>(() =>
    readStoredMs(LOCAL_STORAGE_KEYS.KANBAN_LAST_REFRESH_AT)
  );

  const applyRef = useRef(apply);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const bumpScheduleRef = useRef(false);
  const prevLoadingRef = useRef(loading);

  applyRef.current = apply;

  const getNextRefreshAt = useCallback((): number | null => {
    return readStoredMs(LOCAL_STORAGE_KEYS.KANBAN_NEXT_REFRESH_AT);
  }, []);

  const updateCountdown = useCallback(() => {
    const next = getNextRefreshAt();
    if (next == null) {
      setSecondsUntilRefresh(null);
      return;
    }
    setSecondsUntilRefresh(Math.max(0, Math.ceil((next - Date.now()) / 1000)));
  }, [getNextRefreshAt]);

  const clearTimers = useCallback(() => {
    if (timeoutRef.current != null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (tickRef.current != null) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  const scheduleAutoRefresh = useCallback(() => {
    clearTimers();
    if (!hasData) return;

    const nextAt = getNextRefreshAt();
    const now = Date.now();

    if (nextAt == null) return;

    if (nextAt <= now) {
      bumpScheduleRef.current = true;
      applyRef.current();
      return;
    }

    updateCountdown();
    tickRef.current = setInterval(updateCountdown, 1000);

    timeoutRef.current = setTimeout(() => {
      bumpScheduleRef.current = true;
      applyRef.current();
    }, nextAt - now);
  }, [hasData, clearTimers, getNextRefreshAt, updateCountdown]);

  const triggerRefresh = useCallback(() => {
    bumpScheduleRef.current = true;
    applyRef.current();
  }, []);

  /* Conclusão de fetch: última atualização; reinicia ciclo só se foi refresh manual/automático. */
  useEffect(() => {
    const wasLoading = prevLoadingRef.current;
    prevLoadingRef.current = loading;

    if (wasLoading && !loading && hasData) {
      const now = Date.now();
      writeStoredMs(LOCAL_STORAGE_KEYS.KANBAN_LAST_REFRESH_AT, now);
      setLastRefreshedAt(now);

      const storedNext = getNextRefreshAt();
      if (bumpScheduleRef.current) {
        bumpScheduleRef.current = false;
        writeStoredMs(
          LOCAL_STORAGE_KEYS.KANBAN_NEXT_REFRESH_AT,
          now + SUPRIMENTOS_KANBAN_AUTO_REFRESH_MS
        );
      } else if (storedNext == null) {
        writeStoredMs(
          LOCAL_STORAGE_KEYS.KANBAN_NEXT_REFRESH_AT,
          now + SUPRIMENTOS_KANBAN_AUTO_REFRESH_MS
        );
      }

      scheduleAutoRefresh();
    }
  }, [loading, hasData, scheduleAutoRefresh, getNextRefreshAt]);

  /* Reagenda a partir do horário persistido quando há dados no quadro. */
  useEffect(() => {
    if (!hasData) {
      clearTimers();
      setSecondsUntilRefresh(null);
      return;
    }
    scheduleAutoRefresh();
    return clearTimers;
  }, [hasData, scheduleAutoRefresh, clearTimers]);

  return {
    secondsUntilRefresh,
    lastRefreshedAt,
    triggerRefresh,
    refreshIntervalMinutes: SUPRIMENTOS_KANBAN_AUTO_REFRESH_MS / 60_000,
  };
}
